import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FiDollarSign, FiPlus } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useScopeId } from "@core/hooks/useRouteParamId";
import InfoTooltip from "@core/components/InfoTooltip";
import StatusPill from "@core/components/StatusPill";
import { useFinanceCategoryAction } from "@features/admin/finance/category/hooks/useFinanceCategoryAction";
import { FinanceCategory } from "@features/admin/finance/category/types/financeCategory";
import { CreateFinanceCategoryRequest } from "@features/admin/finance/category/types/financeCategoryRequests";
import DeductibleConfigEditor from "@features/admin/finance/components/DeductibleConfigEditor";
import { showSaveWarnings, useDeductibleConfigForm } from "@features/admin/finance/hooks/useDeductibleConfigForm";

type FieldErrors = Partial<Record<string, string[]>>;

interface BasicForm {
    category_name: string;
    category_description: string;
    range_from: string;
    range_to: string;
}

const EMPTY_FORM: BasicForm = { category_name: "", category_description: "", range_from: "", range_to: "" };

interface FinanceCategoryFormModalProps {
    show: boolean;
    onHide: () => void;
    /** Null for create. */
    category: FinanceCategory | null;
    onSaved?: () => void | Promise<void>;
    testId: string;
}

const FinanceCategoryFormModal: React.FC<FinanceCategoryFormModalProps> = ({ show, onHide, category, onSaved, testId }) => {
    const isEdit = category !== null;
    const prefix = isEdit ? "update_" : "";
    const resolvedNationId = useScopeId() ?? 0;
    const [form, setForm] = useState<BasicForm>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doCreate, doUpdate } = useFinanceCategoryAction();
    const deductibles = useDeductibleConfigForm(show, category?.deductibles);

    useEffect(() => {
        setForm(category ? {
            category_name: category.category_name ?? "",
            category_description: category.category_description ?? "",
            range_from: category.range_from ?? "",
            range_to: category.range_to ?? "",
        } : EMPTY_FORM);
        setFormErrors({});
    }, [category, show]);

    const handleChange = (field: keyof BasicForm) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
        if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleClose = () => {
        setFormErrors({});
        onHide();
    };

    const handleSubmit = async () => {
        const errors: FieldErrors = {};
        const messages: string[] = [];
        if (!form.category_name.trim()) {
            errors.category_name = ["Category name is required."];
            messages.push("Category name is required.");
        }
        if (!form.range_from) {
            errors.range_from = ["Range from is required."];
            messages.push("Range from is required.");
        }
        if (messages.length > 0) {
            setFormErrors(errors);
            showValidation(messages);
            return;
        }

        const payload: CreateFinanceCategoryRequest = {
            category_name: form.category_name,
            category_description: form.category_description || undefined,
            range_from: form.range_from,
            range_to: form.range_to || undefined,
            deductibles: deductibles.buildPayload(),
        };

        try {
            const response = isEdit
                ? await doUpdate(resolvedNationId, category.category_group_id, payload)
                : await doCreate(resolvedNationId, payload);

            if (response.isSuccess()) {
                const data = response.getData();
                showSuccess(data?.message ?? "");
                showSaveWarnings(data);
                if (!isEdit) {
                    setForm(EMPTY_FORM);
                    deductibles.reset();
                }
                handleClose();
                await onSaved?.();
            } else {
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const deductibleMessages = deductibles.applyServerErrors(fieldErrors, payload.deductibles);
                const otherMessages = Object.entries(fieldErrors)
                    .filter(([key]) => !key.startsWith("deductibles"))
                    .flatMap(([, list]) => list ?? [])
                    .filter(Boolean) as string[];
                const list = [...otherMessages, ...deductibleMessages];
                showValidation(list.length ? list : [error?.detail || `Failed to ${isEdit ? "update" : "create"} finance category.`]);
                showError(error);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered size="lg" data-testid={testId}>
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Modal.Title className="fw-semibold mb-0">
                        <FiDollarSign className="me-2" aria-hidden />{isEdit ? "Edit Finance Category" : "Create Finance Category"}
                    </Modal.Title>
                    {category && <StatusPill active={category.is_active} />}
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                <Form>
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Basic Info</h6>
                            <Form.Group className="mb-3" controlId={`${prefix}category_name`}>
                                <Form.Label>
                                    Category Name <span className="text-danger">*</span>
                                    <InfoTooltip text="A descriptive name for this income band." />
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter category name"
                                    value={form.category_name}
                                    onChange={handleChange("category_name")}
                                    isInvalid={!!formErrors.category_name}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.category_name?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0" controlId={`${prefix}category_description`}>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Enter description (optional)"
                                    value={form.category_description}
                                    onChange={handleChange("category_description")}
                                    isInvalid={!!formErrors.category_description}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.category_description?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Range & Bands</h6>
                            <Row>
                                <Col xs={12} md={6}>
                                    <Form.Group className="mb-3" controlId={`${prefix}range_from`}>
                                        <Form.Label>Range From <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number" placeholder="e.g. 0.01" min="0.01" step="0.01"
                                            value={form.range_from} onChange={handleChange("range_from")}
                                            isInvalid={!!formErrors.range_from}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.range_from?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group className="mb-3" controlId={`${prefix}range_to`}>
                                        <Form.Label>Range To</Form.Label>
                                        <Form.Control
                                            type="number" placeholder="Leave blank for open-ended" min="0.01" step="0.01"
                                            value={form.range_to} onChange={handleChange("range_to")}
                                            isInvalid={!!formErrors.range_to}
                                        />
                                        <Form.Control.Feedback type="invalid">{formErrors.range_to?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <DeductibleConfigEditor {...deductibles.editorProps} />

                    <div className="d-flex justify-content-end mt-2">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={handleSubmit}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy ? (
                                <><Spinner animation="border" size="sm" className="me-2" />{isEdit ? "Updating…" : "Creating…"}</>
                            ) : isEdit ? (
                                "Update Category"
                            ) : (
                                <><FiPlus className="me-2" />Create Category</>
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default FinanceCategoryFormModal;
